import {
    AlertModal,
    Box,
    Button,
    deriveColumnWidth,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownTrigger,
    EmptyState,
    IconButton,
    Kebab,
    LinkText,
    Table,
    TextCell,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";
import { toast } from "react-toastify";
import { DateRange } from "rsuite/DateRangePicker";
import { endOfDay, startOfDay, subDays } from "rsuite/esm/internals/utils/date";

import deletionReportTableStyle from "./audit-log-table.scss";
import DeletionReportDetailDialog from "./DeletionReportDetailDialog";
import CustomDatePickerView from "components/common-date-picker/CustomDatePicker";
import { exportFile } from "components/export/common";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import layoutStyle from "components/layout/layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import style from "components/reports/erasure-reports-table.scss";
import DateCell from "components/table/DateCell";
import { DeletionReport } from "domain/auditLog";
import { deletionReportsService, extractFilename } from "services/audit-log/AuditLogService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import tableStyle from "styles/layout.scss";
import { toUtcDateString } from "utils/format";
import { getObject, RepositoryKey } from "utils/repository";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    refreshCount: number;
}

export default function DeletionReportsTable(props: Props) {
    const { t } = useTranslation();
    const theme = useTheme();
    const defaultDateRange: DateRange = [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())];
    const [initialLoading, setInitialLoading] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [requestFailureMessage, setRequestFailureMessage] = React.useState("");
    const [reports, setReports] = React.useState<DeletionReport[]>([]);
    const [nextCursor, setNextCursor] = React.useState<string | undefined>(undefined);
    const [selectedReport, setSelectedReport] = React.useState<DeletionReport | undefined>();
    const [sortAscending, setSortAscending] = React.useState(() => {
        const saved = getObject<{ sortBy?: { id: string; desc: boolean }[] }>(RepositoryKey.DELETION_REPORTS_TABLE);
        return saved?.sortBy?.[0]?.desc === false;
    });
    const [errorModal, setErrorModal] = React.useState({ isOpen: false, title: "" });
    const [scrollToRow, setScrollToRow] = React.useState<number | undefined>(undefined);
    const [dateFilter, setDateFilter] = React.useState<{ from?: string; to?: string }>({
        from: toUtcDateString(defaultDateRange[0]),
        to: toUtcDateString(defaultDateRange[1]),
    });
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        return () => abortControllers.forEach((controller) => controller.abort());
    }, []);

    const fetchData = (
        startDate?: string,
        endDate?: string,
        ascending?: boolean,
        loadMoreCursor?: string,
        abortController?: AbortController
    ) => {
        const controller = abortController ?? new AbortController();
        if (!abortController) {
            abortControllers.push(controller);
        }
        loadMoreCursor ? setLoading(true) : setInitialLoading(true);
        deletionReportsService
            .fetchDeletionReports(startDate, endDate, ascending ? "ASC" : "DESC", loadMoreCursor, controller)
            .then((result) => {
                setReports((prev) => {
                    if (loadMoreCursor) {
                        setScrollToRow(prev.length - 1);
                        return [...prev, ...result.reports];
                    }
                    setScrollToRow(undefined);
                    return result.reports;
                });
                setNextCursor(result.nextCursor);
                setRequestFailureMessage("");
            })
            .catch(() => {
                if (controller.signal.aborted) {
                    return;
                }
                setRequestFailureMessage(t("AuditLogs.deletionReports.requestFailed"));
            })
            .finally(() => {
                if (controller.signal.aborted) {
                    return;
                }
                setInitialLoading(false);
                setLoading(false);
            });
    };

    React.useEffect(() => {
        const abortController = new AbortController();
        setReports([]);
        setNextCursor(undefined);
        fetchData(dateFilter.from, dateFilter.to, sortAscending, undefined, abortController);
        return () => abortController.abort();
    }, [dateFilter, sortAscending, props.refreshCount]);

    const handleDownload = (report: DeletionReport) => {
        usageStatisticsService.sendEvent({ category: Category.AUDIT_LOGS, action: Action.DOWNLOAD_DELETION_REPORT });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        deletionReportsService
            .downloadDeletionReport(report.uuid, abortController)
            .then((response) => {
                const filename = extractFilename(response.headers.get("Content-Disposition"));
                return response.blob().then((blob) => {
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    exportFile(url, filename);
                    window.URL.revokeObjectURL(url);
                    toast(
                        <SuccessIcon
                            successClass={layoutStyle.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={t("AuditLogs.deletionReports.downloadSuccess", { uuid: report.uuid })}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layoutStyle.customToastSuccessMessage,
                        }
                    );
                });
            })
            .catch(() => {
                if (abortController.signal.aborted) {
                    return;
                }
                setErrorModal({ isOpen: true, title: t("AuditLogs.deletionReports.downloadFailed") });
            });
    };

    const columns: Column<DeletionReport>[] = [
        {
            Header: () => <span>{t("AuditLogs.deletionReports.detail.uuid")}</span>,
            accessor: "uuid",
            disableSortBy: true,
            width: deriveColumnWidth(40, tableContainerRef),
            Cell: ({
                cell: {
                    row: { original: report },
                    value,
                },
            }: {
                cell: { row: { original: DeletionReport }; value: string };
            }) => (
                <Box gap="Xs" verticalAlignment="Center">
                    <DropdownMenu
                        testId={testIds.common.primaryView.table.kebabMenu.itself}
                        position="bottom-start"
                        dropdownOffset="Xxs"
                        closeOnSelection
                        flipOptions={{ fallbackPlacements: ["top-start"] }}
                    >
                        <DropdownTrigger>
                            <IconButton>
                                <Kebab />
                            </IconButton>
                        </DropdownTrigger>
                        <DropdownContent>
                            <DropdownMenuItem
                                testId={testIds.workArea.auditLogs.deletionReports.table.kebabMenu.viewButton}
                                onClick={() => setSelectedReport(report)}
                            >
                                {t("Common.view")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                testId={testIds.workArea.auditLogs.deletionReports.table.kebabMenu.downloadButton}
                                onClick={() => handleDownload(report)}
                            >
                                {t("Common.download")}
                            </DropdownMenuItem>
                        </DropdownContent>
                    </DropdownMenu>
                    <TextCell>
                        <LinkText as="button" onClick={() => setSelectedReport(report)}>
                            {value.toLowerCase()}
                        </LinkText>
                    </TextCell>
                </Box>
            ),
        },
        {
            Header: () => t("AuditLogs.deletionReports.detail.username"),
            accessor: "username",
            disableSortBy: true,
            width: deriveColumnWidth(25, tableContainerRef),
            Cell: ({ cell: { value } }) => value,
        },
        {
            Header: () => t("AuditLogs.deletionReports.detail.logCount"),
            accessor: "logCount",
            disableSortBy: true,
            width: deriveColumnWidth(10, tableContainerRef),
            Cell: ({ cell: { value } }) => value,
        },
        {
            Header: () => t("AuditLogs.deletionReports.detail.creationTime"),
            accessor: "creationTime",
            width: deriveColumnWidth(25, tableContainerRef),
            Cell: ({ cell: { value } }) => <DateCell value={value} />,
        },
    ];

    return (
        <>
            <div className={deletionReportTableStyle.layout}>
                <div className={classNames(style.tableDetailsGrid, style.gridContentEnd)}>
                    <div className={style.filter}>
                        <CustomDatePickerView
                            defaultDateRange={defaultDateRange}
                            defaultPlaceholder={true}
                            editable={true}
                            category={Category.AUDIT_LOGS}
                            onUpdate={(from?: string, to?: string) => {
                                setReports([]);
                                setNextCursor(undefined);
                                setDateFilter({ from, to });
                            }}
                        />
                    </div>
                </div>

                <div className={tableStyle.tableWrapper}>
                    <div
                        className={classNames(tableStyle.tableContainer, deletionReportTableStyle.tableContainer)}
                        ref={tableContainerRef}
                    >
                        <Table
                            tableIdentity={RepositoryKey.DELETION_REPORTS_TABLE}
                            data={reports}
                            columns={columns}
                            loaded={!initialLoading}
                            emptyStateMessage={<EmptyState text={t("AuditLogs.deletionReports.emptyStateMessage")} />}
                            failureMessage={requestFailureMessage}
                            testIds={{ itself: testIds.workArea.auditLogs.deletionReports.table.itself }}
                            scrollTo={scrollToRow}
                            sortCallbacks={{
                                onChange: (_col, ascending) => setSortAscending((prev) => ascending ?? prev),
                                onDisable: () => setSortAscending(false),
                            }}
                            sortTooltipTexts={{
                                ascending: t("Common.ascending"),
                                descending: t("Common.descending"),
                                default: t("Common.sortContent"),
                            }}
                        />
                    </div>
                </div>

                {nextCursor &&
                    (loading ? (
                        <LoadingIndicator small={true} withTable />
                    ) : (
                        <Box horizontalAlignment="Center" marginTop="Sm">
                            <Button
                                variant="Primary"
                                onClick={() => fetchData(dateFilter.from, dateFilter.to, sortAscending, nextCursor)}
                                data-testid={testIds.common.primaryView.table.loadMoreButton}
                            >
                                {t("Common.loadMore")}
                            </Button>
                        </Box>
                    ))}
            </div>

            <DeletionReportDetailDialog report={selectedReport} onClose={() => setSelectedReport(undefined)} />
            <AlertModal
                title={errorModal.title}
                variant="Warning"
                isOpen={errorModal.isOpen}
                hideModal={() => setErrorModal({ isOpen: false, title: "" })}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setErrorModal({ isOpen: false, title: "" })}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
            >
                <Box marginTop="Sm">
                    {t("Common.genericErrorMessage")}
                    {t("Common.genericContactSupport")}
                </Box>
            </AlertModal>
        </>
    );
}
